import { useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router';
import { TriangleAlert } from 'lucide-react';

import { useAuthStore } from '@auth/authStore';
import { PageTabs, LoadingScreen, UnderDevelopment } from '@components';
import { useAsyncResource, useUrlAssetScope } from '@hooks';
import { getAnomalies } from '@entities';

import { AnomalyRegister } from '@features/anomaly-register';
import { AnomalyViewer } from '@features/anomaly-viewer';
import { anomalyColumnsCompact } from './AnomalySuite.columns';

import styles from './AnomalySuite.module.css';

const tabs = [
  { id: "register", label: "Anomaly Register" },
  { id: "viewer", label: "3D Viewer" },
  { id: "tracking", label: "Anomaly Tracking" },
  { id: "overview", label: "Anomaly Overview" },
];

function AnomalySuite() {
  const companyId = useAuthStore(state => state.companyID);
  const { siteId, assetId } = useUrlAssetScope();
  const [searchParams, setSearchParams] = useSearchParams();

  const requestedView = searchParams.get('anomalyView');

  const view = tabs.some(tab => tab.id === requestedView)
    ? requestedView
    : 'register';

  useEffect(() => {
    const alreadyNormalized =
      searchParams.get('anomalyView') === view &&
      !searchParams.has('tab');

    if (alreadyNormalized) return;

    setSearchParams(previous => {
      const next = new URLSearchParams(previous);

      next.set('anomalyView', view);
      next.delete('tab');

      return next;
    }, { replace: true })
  }, [view, searchParams, setSearchParams]);

  function handleViewChange(nextView) {
    if (
      nextView === view ||
      !tabs.some(tab => tab.id === nextView)
    ) {
      return;
    }

    setSearchParams(previous => {
      const next = new URLSearchParams(previous);

      next.set('anomalyView', nextView);
      next.delete('tab');

      return next;
    });
  }

  const resource = useAsyncResource(
    async () => {
      const rows = await getAnomalies({ companyId });

      if (!Array.isArray(rows)) {
        throw new Error('The anomaly API must return a list.');
      }

      return { companyId, rows };
    },
    [companyId],
    {
      initialData: null,
      enabled: Boolean(companyId),
    }
  );

  const anomalyData = useMemo(() => {
    return resource.data?.companyId === companyId
      ? resource.data.rows
      : [];
  }, [resource.data, companyId])

  const scopedAnomalies = useMemo(() => {
    return anomalyData.filter(entry => {
      const matchesSite =
        !siteId || String(entry.site_id) === siteId;

      const matchesAsset =
        !assetId || String(entry.asset_id) === assetId;

      return matchesSite && matchesAsset;
    });
  }, [anomalyData, siteId, assetId]);

  function renderActiveView() {
    if (view === 'tracking') {
      return (
        <UnderDevelopment featureName='Anomaly Tracking' />
      );
    }

    if (view === 'overview') {
      return (
        <UnderDevelopment featureName='Anomaly Overview' />
      );
    }

    if (!companyId) {
      return (
        <div className={styles.feedback} role="status">
          No company is selected.
        </div>
      );
    }

    if (resource.isLoading) {
      return <LoadingScreen />
    }

    if (resource.error) {
      return (
        <div className={styles.feedback} role="alert">
          <TriangleAlert
            className={styles.errorIcon}
            aria-hidden="true"
          />

          <p>{resource.error}</p>

          <button
            type="button"
            className={styles.retryButton}
            onClick={() => resource.reload()}
          >
            Retry
          </button>
        </div>
      );
    }

    if (view === "viewer") {
      return <AnomalyViewer data={scopedAnomalies} />
    }

    return (
      <AnomalyRegister
        data={anomalyData}
        columns={anomalyColumnsCompact}
      />
    );
  }

  return (
    <div className={styles.page}>
      <PageTabs
        id="anomaly-views"
        label="Anomaly Suite views"
        tabs={tabs}
        current={view}
        onChange={handleViewChange}
      />

      {tabs.map(tab => (
        <section
          key={tab.id}
          id={`anomaly-views-panel-${tab.id}`}
          role="tabpanel"
          aria-labelledby={`anomaly-views-tab-${tab.id}`}
          hidden={view !== tab.id}
          tabIndex={0}
          className={styles.panel}
        >
          {tab.id === 'viewer'
            ? (
              companyId && !resource.isLoading && !resource.error
                ? (
                  <AnomalyViewer
                    key={companyId}
                    data={scopedAnomalies}
                    isActive={view === 'viewer'}
                  />
                )
                : (view === 'viewer' ? renderActiveView() : null)
            )
            : (view === tab.id ? renderActiveView() : null)}
        </section>
      ))}
    </div>
  )
}

export default AnomalySuite;